/**
 * apiError.ts — canonical error envelope type, type guard, and error extractor.
 *
 * Owns: the `ApiErrorResponse` shape that the backend sends on failure
 *       (per `refactor/api-contract.md`), the `isApiErrorResponse` type
 *       guard for narrowing untyped values, and the `extractApiError`
 *       helper that consolidates the "read the user-facing error string
 *       out of an unknown caught value" pattern that previously lived
 *       inline at 140+ frontend call sites.
 * Used by: every frontend handler that needs to surface an API error to
 *       the user — toast.error, alert, snackbar, inline error banners.
 *       Replaces the inline cast + optional-chain + fallback chain.
 * Depends on: nothing. Pure type module + small runtime helper.
 * Notes: introduced by ticket 0b-04. The wire format matches what the
 *       backend's `apiResponse.error()` helper produces; see
 *       `backend/src/utils/apiResponse.ts`. The 2 prior `AppShell.tsx`
 *       cast sites were cleaned up in 0b-02b under the AppShell
 *       frozen-file unlock. Success-path migration (rest of the
 *       TODO(0b-02b) markers) was attempted in 0b-02b but rolled back
 *       after raw-axios consumers in `AuthContext.tsx`,
 *       `UserManagement.tsx`, `TwilioContext.tsx`, etc. surfaced as
 *       broken in the staging browser sanity check; that work moves
 *       to ticket 0b-02c with a comprehensive consumer audit as a
 *       pre-flight requirement.
 */

/**
 * Canonical error envelope shape sent by the backend on failure.
 * See `refactor/api-contract.md` for the contract and
 * `backend/src/utils/apiResponse.ts` for the producer.
 */
export interface ApiErrorResponse {
  success: false;
  error: string;
  code?: string;
  details?: unknown;
}

/**
 * Type guard — returns true when `value` matches the `ApiErrorResponse`
 * shape (object, `success === false`, `error` is a string).
 */
export function isApiErrorResponse(value: unknown): value is ApiErrorResponse {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as { success?: unknown; error?: unknown };
  return v.success === false && typeof v.error === 'string';
}

/**
 * Extract a user-facing error string from an unknown caught value.
 *
 * Resolution order:
 *   1. Direct envelope: `value` itself matches `ApiErrorResponse` (used
 *      by call sites that get an unwrapped envelope back from an api.ts
 *      wrapper that returned the failure envelope instead of throwing).
 *   2. Axios error: `value.response.data` matches `ApiErrorResponse`
 *      (the dominant catch-block pattern — axios throws on 4xx/5xx and
 *      the canonical envelope lives at `response.data`).
 *   3. JS Error / network error: `value.message` is a non-empty string
 *      (covers `TypeError: Failed to fetch`, axios timeout messages, and
 *      anything thrown without a backend response).
 *   4. Fallback: the optional second argument, or `'Something went
 *      wrong'` if none provided.
 */
export function extractApiError(
  err: unknown,
  fallback: string = 'Something went wrong',
): string {
  // 1. Direct envelope (some api.ts wrappers return the unwrapped envelope
  //    rather than throwing on failure).
  if (isApiErrorResponse(err)) return err.error;

  if (err && typeof err === 'object') {
    const e = err as { response?: { data?: unknown }; message?: unknown };

    // 2. Axios error: the canonical envelope at `.response.data`.
    const data = e.response?.data;
    if (isApiErrorResponse(data)) return data.error;

    // 3. JS Error or anything else with a message.
    if (typeof e.message === 'string' && e.message.length > 0) return e.message;
  }

  return fallback;
}
